import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { get } from '../../api/client';
import type { AdminReferralListResponse, AdminReferralRecord, ReferralStatus } from './types';

const STATUS_TABS: Array<{ label: string; value: ReferralStatus | 'all' }> = [
  { label: '全部', value: 'all' },
  { label: '待付费', value: 'pending' },
  { label: '已达标', value: 'qualified' },
  { label: '已计酬', value: 'rewarded' },
];

const STATUS_TAG: Record<string, { text: string; color: string }> = {
  pending: { text: '待付费', color: 'warning' },
  qualified: { text: '已达标', color: 'processing' },
  rewarded: { text: '已计酬', color: 'success' },
};

/** 不计酬 / 反作弊原因 → 中文标记 */
const REASON_TAG: Record<string, { text: string; color: string }> = {
  below_min_amount: { text: '未达最低实付额', color: 'default' },
  monthly_cap: { text: '已达月度上限', color: 'orange' },
};

/**
 * 邀请记录页（管理端）。
 * - GET /admin/referral/records 分页检索邀请记录（含双方展示名、计酬状态、不计酬原因）。
 * - 查询参数一律 camelCase（query 不经任何大小写转换，键名必须与后端 DTO 逐字一致）：
 *   status / page / pageSize；另有 referrerId 可按邀请人过滤，本页 UI 暂未暴露。
 */
export default function ReferralsPage() {
  const [status, setStatus] = useState<ReferralStatus | 'all'>('all');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['referral-records', status, page],
    queryFn: () =>
      get<AdminReferralListResponse>('/admin/referral/records', {
        ...(status === 'all' ? {} : { status }),
        page,
        pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;

  const columns: ColumnsType<AdminReferralRecord> = [
    { title: '邀请人', dataIndex: 'referrerName', width: 150, render: (v: string) => v || '-' },
    { title: '被邀请人', dataIndex: 'inviteeName', width: 150, render: (v: string) => v || '-' },
    {
      title: '邀请码',
      dataIndex: 'referralCode',
      width: 120,
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: ReferralStatus) => {
        const s = STATUS_TAG[v] ?? { text: v, color: 'default' };
        return <Tag color={s.color}>{s.text}</Tag>;
      },
    },
    {
      title: '奖励',
      dataIndex: 'rewardMonths',
      width: 100,
      render: (v: number) => (v > 0 ? <Typography.Text strong>+{v} 个月</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: '标记',
      dataIndex: 'reason',
      width: 150,
      render: (v: string | null) => {
        if (!v) return <Typography.Text type="secondary">—</Typography.Text>;
        const r = REASON_TAG[v] ?? { text: v, color: 'default' };
        return <Tag color={r.color}>{r.text}</Tag>;
      },
    },
    {
      title: '关联订单',
      dataIndex: 'orderId',
      width: 120,
      render: (v: string | null) =>
        v ? (
          <Tooltip title={v}>
            <Typography.Text code>{v.slice(0, 8)}</Typography.Text>
          </Tooltip>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: '绑定时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          邀请记录
        </Typography.Title>
        <Typography.Text type="secondary">
          查看用户间的邀请绑定关系与计酬状态。仅真实付费成功才计酬；反作弊标记（未达最低实付额 / 超月度上限）会自动拦截发放。
        </Typography.Text>
      </div>

      <Card styles={{ body: { paddingTop: 16 } }}>
        <Space style={{ marginBottom: 16 }}>
          <Segmented
            options={STATUS_TABS}
            value={status}
            onChange={(v) => {
              setStatus(v as ReferralStatus | 'all');
              setPage(1);
            }}
          />
        </Space>

        <Table<AdminReferralRecord>
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 1060 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            onChange: (p) => setPage(p),
            showTotal: (t) => `共 ${t} 条邀请记录`,
          }}
          locale={{ emptyText: '暂无邀请记录' }}
        />
      </Card>
    </div>
  );
}
